import { ChangeDetectionStrategy, Component, Injector, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { RoleConnection } from "@common/ADAPT.Common.Model/organisation/role-connection";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { TeamLocation } from "@common/ADAPT.Common.Model/organisation/team-location";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { AutosaveComponent } from "@common/ux/autosave/autosave.component";
import { BaseRoutedComponent } from "@common/ux/base-routed.component";
import { OrganisationMapService } from "@org-common/lib/architecture/organisation-map/organisation-map.service";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { ConfigurationAuthService } from "@org-common/lib/configuration/configuration-auth.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { CommonTeamsAuthService } from "@org-common/lib/teams/common-teams-auth.service";
import { TeamsUiService } from "app/features/people/teams/teams-ui.service";
import { teamDashboardPageRoute } from "app/platform/team-dashboard/team-dashboard-page/team-dashboard-page.route";
import { auditTime, BehaviorSubject, combineLatest, merge } from "rxjs";
import { debounceTime, map, skip, startWith, switchMap } from "rxjs/operators";
import { IOrganisationMapFilterParams, OrganisationMapFilterService } from "../organisation-map-filter/organisation-map-filter.service";

interface IViewOption {
    option: number;
    icon: string;
    text: string;
}

@Component({
    templateUrl: "./organisation-map-page.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class OrganisationMapPageComponent extends BaseRoutedComponent implements OnInit, OnDestroy {
    // static so we remember it between navigations
    public static isEditing = false;

    public readonly availableViewOptions: IViewOption[] = [
        { option: 1, icon: "", text: "1st level" },
        { option: 2, icon: "", text: "2nd level" },
        { option: 3, icon: "", text: "3rd level" },
        { option: 4, icon: "", text: "4th level" },
        { option: 0, icon: "", text: "Everything" },
    ];

    public pauseSaving = false;
    public canConfigureTeam = false;
    public filterBeforeEditingIsDefault = true;
    private filterBeforeEditing?: IOrganisationMapFilterParams;
    private myTeams$ = new BehaviorSubject<Set<number>>(new Set());

    @ViewChild(AutosaveComponent) public autosaveComponent?: AutosaveComponent;

    public constructor(
        private teamsUiService: TeamsUiService,
        private authService: AuthorisationService,
        private organisationMapFilterService: OrganisationMapFilterService,
        private commonDataService: CommonDataService,
        injector: Injector,
        teamsService: CommonTeamsService,
        rxjsBreezeService: RxjsBreezeService,
        private orgMapService: OrganisationMapService,
    ) {
        super(injector);

        this.navigationEnd.pipe(
            // only act after first navigation event (as that one is handled by ngOnInit)
            skip(1),
            auditTime(100),
            switchMap(async () => {
                if (!this.organisationMapFilterService.isDefault) {
                    await this.organisationMapFilterService.setFilterToUrl();
                }
            }),
            this.takeUntilDestroyed(),
        ).subscribe();

        combineLatest([
            this.authService.getHasAccess(CommonTeamsAuthService.ConfigureTeam),
            this.authService.getHasAccess(ConfigurationAuthService.ConfigureOrganisation),
        ]).pipe(
            this.takeUntilDestroyed(),
        ).subscribe(([configureTeam, configureOrg]) => this.canConfigureTeam = configureTeam || configureOrg);

        merge(
            rxjsBreezeService.entityTypeChanged(Team),
            rxjsBreezeService.entityTypeChanged(RoleConnection),
            orgMapService.hierarchyChanged$,
        ).pipe(
            debounceTime(100),
            startWith(undefined), // initial get
            switchMap(() => teamsService.promiseToGetActiveTeamsForCurrentPerson()),
            this.takeUntilDestroyed(),
        ).subscribe((teams) => {
            const teamIds = teams.map((team) => team.teamId);
            this.myTeams$.next(new Set([...teamIds, ...orgMapService.getAncestorTeamIds(teamIds)]));
        });

        orgMapService.mapEntityDragging$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe((entity) => this.pauseSaving = !!entity);

        orgMapService.mapEntityChanged$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe((entities) => this.autosaveComponent?.triggerSave(entities));
    }

    public get isEditing() {
        return OrganisationMapPageComponent.isEditing;
    }

    public set isEditing(isEditing: boolean) {
        OrganisationMapPageComponent.isEditing = isEditing;
    }

    public get viewOption() {
        return this.organisationMapFilterService.filter$.value.view;
    }

    public get supplementaryDataVisible() {
        return this.organisationMapFilterService.filter$.value.supplementaryData;
    }

    public async ngOnInit() {
        this.organisationMapFilterService.setInitialFilter();
        if (this.isEditing) {
            await this.onEditToggleChange(true);
        }
        await this.organisationMapFilterService.setFilterToUrl();
        this.notifyActivated();
    }

    public ngOnDestroy() {
        this.commonDataService.enableSaveOverlayForEntityTypes(TeamLocation);
        super.ngOnDestroy();
    }

    public async onViewChanged(updatedOption: number) {
        if (updatedOption < 0) {
            updatedOption = 4;
        }

        if (updatedOption > 4) {
            updatedOption = 0;
        }

        await this.organisationMapFilterService.updateFilter({
            ...this.organisationMapFilterService.filter$.value,
            view: updatedOption,
        });
    }

    public async onEditToggleChange(isEditing: boolean) {
        if (isEditing) {
            this.filterBeforeEditing = this.organisationMapFilterService.filter$.value;
            this.filterBeforeEditingIsDefault = this.organisationMapFilterService.isDefault;

            // make sure everything is visible
            await this.organisationMapFilterService.updateFilter({
                showMyTeams: false,
                showPrivateTeams: true,
                showEndedTeams: false,
                // TODO: should this be set to "Everything" (0)?
                view: this.filterBeforeEditing.view,
                supplementaryData: [],
            });
        } else {
            // user toggled editing off, so try save changes immediately (skipping the wait)
            this.autosaveComponent?.triggerImmediateSave().subscribe();

            if (this.filterBeforeEditing) {
                await this.organisationMapFilterService.updateFilter(this.filterBeforeEditing);
            } else {
                this.organisationMapFilterService.reset();
            }
        }
    }

    @Autobind
    public async addTeam() {
        const newTeam = await this.teamsUiService.promiseToCreateTeam();
        // newTeam will be undefined if clicking "Cancel" on the creation dialog
        if (newTeam) {
            teamDashboardPageRoute.gotoRoute({ teamId: newTeam.teamId }).subscribe();
        }
    }

    @Autobind
    public filterForTeams(allTeams: Team[]) {
        return combineLatest([
            this.organisationMapFilterService.filter$,
            this.myTeams$,
            this.orgMapService.hierarchyChanged$.pipe(startWith(undefined)),
        ]).pipe(
            map(([filter, myTeamIds]) => {
                const filteredTeams = allTeams.filter((team) => (filter.showEndedTeams || team.isActive()
                    || this.orgMapService.teamHasChildren(team.teamId))
                    && (filter.showPrivateTeams || !team.isPrivate));
                if (!filter.showMyTeams) {
                    return filteredTeams;
                }

                return filteredTeams.filter((team) => myTeamIds.has(team.teamId));
            }),
        );
    }
}
